import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle2, IndianRupee, Lock, ShieldCheck, Phone, MapPin, ExternalLink, MessageCircle, AlertOctagon, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import { SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY, buildSupportWhatsAppLink } from "@/lib/support";

export default function Booking() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [timingAck, setTimingAck] = useState(false);
  const [notif, setNotif] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    const r = await api.get("/bookings/my");
    const found = r.data.find((x) => x.id === id);
    setB(found);
    if (found?.payment_status === "paid") {
      const n = await api.get("/notifications").catch(() => ({ data: [] }));
      setNotif(n.data.find((x) => x.booking_id === id));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!b) return <div className="min-h-screen bg-[#FBF5EA]"><Navbar /><div className="p-20 text-center text-slate-500">Loading…</div></div>;

  // Pay a subsequent quarterly instalment (Q2/Q3/Q4). Reuses Razorpay flow.
  const payInstalment = async (seq) => {
    try {
      const { data: order } = await api.post(`/bookings/${id}/instalments/${seq}/order`);
      if (!window.Razorpay) return toast.error("Payment SDK not loaded — refresh and retry");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        order_id: order.order_id, name: "TheHostelNow",
        description: `${b.hostel_name} · Q${seq} instalment · ₹${order.amount_inr}`,
        image: "/icon-192.png",
        theme: { color: "#9A3412" },
        handler: async (r) => {
          try {
            await api.post(`/bookings/${id}/instalments/${seq}/verify`, {
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            toast.success(`Q${seq} instalment paid ✓`);
            load();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Verification failed");
          }
        },
      });
      rzp.on("payment.failed", (resp) => toast.error(resp.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create payment order");
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      // 1) Ask backend to create a Razorpay order
      const orderRes = await api.post(`/bookings/${id}/razorpay-order`);
      const { order_id, amount, currency, key_id, prefill } = orderRes.data;

      if (!window.Razorpay) {
        toast.error("Payment SDK failed to load. Refresh and try again.");
        setPaying(false);
        return;
      }

      // 2) Open Razorpay Checkout
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        order_id,
        name: "HostelNow",
        description: `${b.hostel_name} · ${b.nights} night(s) · ${b.beds} bed(s)`,
        image: "/icon-192.png",
        prefill,
        theme: { color: "#9A3412" },
        handler: async (response) => {
          try {
            // 3) Verify signature server-side and confirm booking
            const { data: pay } = await api.post(`/bookings/${id}/pay`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful! Your bed is confirmed.");
            // Use the response payload directly so the confirmation panel renders
            // even if /notifications and /bookings/my re-fetches are slow or fail.
            if (pay?.verification_code) {
              setNotif({
                booking_id: id,
                verification_code: pay.verification_code,
                owner_name: pay.owner_name,
                owner_phone: pay.owner_phone,
                location: [pay.hostel_address, pay.hostel_city].filter(Boolean).join(", "),
                maps_link: pay.maps_link,
                whatsapp_owner_link: pay.whatsapp_owner_link,
                whatsapp_self_link: pay.whatsapp_self_link,
                email_sent_to_student: pay.email_sent_to_student,
                email_sent_to_owner: pay.email_sent_to_owner,
              });
              // Also merge into `b` so the verification-code panel renders immediately
              setB((prev) => prev ? { ...prev, payment_status: "paid", verification_code: pay.verification_code, payment_ref: response.razorpay_payment_id } : prev);
            }
            // Best-effort refresh
            load().catch(() => {});
            // Auto-open WhatsApp self-receipt on mobile
            if (pay?.whatsapp_self_link) {
              window.open(pay.whatsapp_self_link, "_blank", "noopener,noreferrer");
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          } finally { setPaying(false); }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info("Payment cancelled. You can retry anytime.");
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Unknown error"}`);
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  const paid = b.payment_status === "paid";

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!paid && (
          <div data-testid="unpaid-warning-banner" className="mb-6 rounded-2xl border-2 border-dashed border-rose-400 bg-rose-50 p-4 flex items-start gap-3">
            <AlertOctagon size={22} className="flex-shrink-0 mt-0.5 text-rose-600" />
            <div className="text-sm">
              <div className="font-black uppercase tracking-widest text-rose-800 text-xs">Booking not confirmed · payment required</div>
              <p className="mt-1 text-rose-900">This is only a reservation slot — no money has moved yet. <strong>The hostel owner will not accept this page as proof of payment.</strong> Complete the Razorpay payment below to receive a confirmed booking with a receipt & verification code.</p>
            </div>
          </div>
        )}
        <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Confirm your booking</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">
          {paid ? "You're all set." : "One last step."}
        </h1>

        <div className="mt-10 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-6 flex gap-5 border-b border-slate-200">
            <img src={b.hostel_image} className="w-32 h-24 rounded-xl object-cover" alt="" />
            <div>
              <div className="font-display font-bold text-xl">{b.hostel_name}</div>
              <div className="text-sm text-slate-500">{b.hostel_city}</div>
              <div className="text-sm mt-2">{b.check_in} → {b.check_out} · {b.nights} nights · {b.beds} bed(s)</div>
              {b.room_type_name && (
                <div data-testid="booking-room-type-badge" className="mt-2 inline-flex items-center gap-2 rounded-full bg-marigold-50 border border-marigold-300 px-3 py-1 text-xs font-bold text-vermilion-700">
                  Room type: {b.room_type_name}
                  <span className="text-slate-500 font-normal">· ₹{Number(b.room_type_price_per_month).toLocaleString("en-IN")}/month</span>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 space-y-2">
            <div className="flex justify-between text-slate-600">
              <span>
                {b.room_type_name
                  ? <>{b.room_type_name} · ₹{Number(b.room_type_price_per_month).toLocaleString("en-IN")} × {b.room_type_months_charged} mo</>
                  : <>{b.room_type ? `${b.room_type} · ` : ""}₹{b.rate_per_bed_night ?? b.hostel_subtotal / Math.max(1, b.nights * b.beds)} × {b.nights} × {b.beds}</>}
              </span>
              <span>₹{(b.hostel_subtotal ?? (b.amount - (b.platform_fee || 0) - (b.meal_cost || 0))).toLocaleString("en-IN")}</span>
            </div>
            {b.meal_included && (
              <div className="flex justify-between text-slate-600">
                <span>Meals · ₹{b.meal_price_per_bed_night} × {b.nights} × {b.beds}</span>
                <span>₹{(b.meal_cost || 0).toLocaleString("en-IN")}</span>
              </div>
            )}
            {b.food_included_in_rent && (
              <div data-testid="booking-food-included-line" className="flex justify-between text-emerald-700 font-semibold">
                <span>Meals · Food included in rent</span>
                <span>₹0</span>
              </div>
            )}
            {(b.platform_fee || 0) > 0 && (
              <div className="flex justify-between text-slate-600"><span>Platform fee</span><span>₹{(b.platform_fee || 0).toLocaleString("en-IN")}</span></div>
            )}
            {b.monthly_discount > 0 && (
              <div data-testid="monthly-discount-line" className="flex justify-between text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <span className="flex items-center gap-1.5">
                  <Sparkles size={14} className="text-amber-500" /> Monthly saver · 10% off room rent
                </span>
                <span>−₹{b.monthly_discount.toLocaleString("en-IN")}</span>
              </div>
            )}
            {b.yearly_discount > 0 && (
              <div data-testid="yearly-discount-line" className="flex justify-between text-emerald-800 font-semibold bg-turmeric-100 border border-turmeric-300 rounded-lg px-3 py-2">
                <span className="flex items-center gap-1.5">
                  <Sparkles size={14} className="text-vermilion-600" /> Yearly saver · 15% off room rent
                </span>
                <span>−₹{b.yearly_discount.toLocaleString("en-IN")}</span>
              </div>
            )}
            <div className="flex justify-between font-display font-black text-2xl pt-3 border-t border-dashed border-slate-200">
              <span>{b.payment_plan === "quarterly" ? "1st instalment due now" : "Total due"}</span>
              <span className="flex items-baseline"><IndianRupee size={18} strokeWidth={3} />{b.amount.toLocaleString("en-IN")}</span>
            </div>
            <div data-testid="payment-no-fees-badge" className="flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 text-[11px] font-bold text-emerald-800">
              <ShieldCheck size={13} className="flex-shrink-0" /> No hidden charges — no booking, convenience or platform fee
            </div>
            {b.payment_plan === "quarterly" && Array.isArray(b.instalments) && (
              <div data-testid="quarterly-schedule" className="rounded-xl border border-turmeric-300 bg-turmeric-50 p-3 space-y-1.5">
                <div className="text-[10px] tracking-widest uppercase font-black text-vermilion-700 flex items-center justify-between">
                  <span>Quarterly plan · 4 instalments</span>
                  <span>Total plan: ₹{(b.total_plan_amount || b.amount).toLocaleString("en-IN")}</span>
                </div>
                {b.instalments.map((i) => {
                  const isNextDue =
                    i.status !== "paid" &&
                    b.instalments.filter((x) => x.status !== "paid").sort((a, c) => a.seq - c.seq)[0]?.seq === i.seq;
                  return (
                    <div key={i.seq} className="flex justify-between items-center text-xs gap-2">
                      <span className="text-slate-700">Q{i.seq} · due {new Date(i.due_date).toLocaleDateString("en-IN")}</span>
                      <span className="flex items-center gap-2">
                        <span className={`font-semibold ${i.status === "paid" ? "text-emerald-700" : "text-slate-800"}`}>
                          ₹{i.amount.toLocaleString("en-IN")} · {i.status === "paid" ? "PAID" : (i.seq === 1 ? "pay now" : "later")}
                        </span>
                        {isNextDue && i.seq !== 1 && (
                          <Button
                            data-testid={`pay-instalment-q${i.seq}`}
                            size="sm"
                            onClick={() => payInstalment(i.seq)}
                            className="h-7 px-2.5 rounded-full text-[10px] bg-vermilion-600 hover:bg-vermilion-700 text-white"
                          >
                            Pay now
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
                <div className="text-[10px] text-slate-500 mt-1">The bed is locked to you as soon as the 1st instalment is paid. We'll email + WhatsApp you before each subsequent due date.</div>
              </div>
            )}
          </div>
          <div className="p-6 bg-slate-50 border-t border-slate-200">
            {paid ? (
              <div>
                <div className="flex items-center gap-3 text-emerald-700">
                  <CheckCircle2 size={22} /> <div>
                    <div className="font-semibold">Payment received via Razorpay</div>
                    <div className="text-sm text-slate-600">Razorpay Ref: <span className="font-mono">{b.payment_ref}</span></div>
                  </div>
                </div>
                {b.verification_code && (
                  <div data-testid="verification-code-panel" className="mt-4 rounded-xl border-2 border-emerald-400 bg-emerald-50 p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1">
                      <ShieldCheck size={12} /> Booking verification code — show at check-in
                    </div>
                    <div data-testid="verification-code-value" className="mt-2 font-mono font-black text-2xl sm:text-3xl tracking-widest text-emerald-800 select-all">
                      {b.verification_code}
                    </div>
                    <div className="mt-2 text-[11px] text-emerald-900 leading-snug">
                      Your hostel owner can verify this code independently at{" "}
                      <a
                        href={`/verify?code=${encodeURIComponent(b.verification_code)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-semibold"
                      >
                        {typeof window !== "undefined" ? window.location.host : "hostelnow.com"}/verify
                      </a>. Screenshots alone are not accepted — only the live-verified code proves this booking is paid.
                    </div>
                  </div>
                )}
                {/* Reminder of the house rules the guest already acknowledged */}
                <div className="mt-3 rounded-xl border border-turmeric-300 bg-turmeric-50 px-3 py-2 text-xs text-stone-800">
                  <div className="flex items-center gap-2 font-bold text-vermilion-700 mb-1">
                    <ShieldCheck size={13} /> House rules (agreed)
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>• Check-in <b>2:00 PM</b></span>
                    <span>• Check-out <b>11:00 AM</b></span>
                    <span>• <b>No smoking</b> on campus</span>
                    <span>• <b>No drinking</b> on campus</span>
                    <span>• <b>No entry after 10 PM</b></span>
                    <span>• <b>No guests</b> allowed inside the hostel premises</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* House rules acknowledgement — must tick before pay */}
                <div
                  data-testid="house-rules-notice"
                  className="rounded-xl border-2 border-turmeric-400 bg-turmeric-50 p-4 mb-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-vermilion-600 text-white grid place-items-center flex-shrink-0">
                      <ShieldCheck size={16} />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-widest uppercase font-black text-vermilion-700">House rules</div>
                      <div className="font-display font-black text-lg text-stone-900 leading-tight">Please read before booking</div>
                    </div>
                  </div>
                  <ul className="space-y-1.5 text-sm text-stone-900">
                    <li className="flex items-start gap-2"><Clock size={14} className="mt-0.5 text-vermilion-600 flex-shrink-0" /> <span><b>Check-in</b> from <b>2:00 PM</b></span></li>
                    <li className="flex items-start gap-2"><Clock size={14} className="mt-0.5 text-vermilion-600 flex-shrink-0" /> <span><b>Check-out</b> by <b>11:00 AM</b></span></li>
                    <li className="flex items-start gap-2"><AlertOctagon size={14} className="mt-0.5 text-vermilion-600 flex-shrink-0" /> <span><b>No smoking</b> anywhere on campus</span></li>
                    <li className="flex items-start gap-2"><AlertOctagon size={14} className="mt-0.5 text-vermilion-600 flex-shrink-0" /> <span><b>No drinking</b> anywhere on campus</span></li>
                    <li className="flex items-start gap-2"><AlertOctagon size={14} className="mt-0.5 text-vermilion-600 flex-shrink-0" /> <span><b>No entry after 10:00 PM</b> — plan your travel around the curfew</span></li>
                    <li className="flex items-start gap-2"><AlertOctagon size={14} className="mt-0.5 text-vermilion-600 flex-shrink-0" /> <span><b>Strictly no guests</b> allowed inside the hostel premises</span></li>
                  </ul>
                  <div className="mt-2 text-[11px] text-stone-700 italic">
                    Violations may result in immediate check-out at your cost without refund.
                  </div>
                  <label className="mt-3 flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="house-rules-ack-checkbox"
                      checked={timingAck}
                      onChange={(e) => setTimingAck(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-vermilion-600"
                    />
                    <span className="text-sm font-semibold text-stone-900">
                      I have read and agree to the house rules.
                    </span>
                  </label>
                </div>
                <div className="flex items-start gap-2 text-xs text-slate-600 mb-3">
                  <Lock size={12} className="mt-0.5 text-orange-800" /> Secured by Razorpay. UPI, cards, netbanking, wallets — 100+ payment methods supported.
                </div>
                <div
                  data-testid="pay-reminder-note"
                  className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2"
                >
                  <AlertOctagon size={12} className="mt-0.5 text-amber-700 flex-shrink-0" />
                  <span>
                    Quick reminder: <b>check-in 2:00 PM</b> · <b>check-out 11:00 AM</b> ·
                    <b> no smoking</b> · <b>no alcohol</b> · <b>no entry after 10 PM</b> · <b>no guests</b> on campus.
                  </span>
                </div>
                <Button
                  data-testid="pay-btn"
                  onClick={pay}
                  disabled={paying || !timingAck}
                  className="w-full h-12 rounded-full bg-amber-600 hover:bg-amber-700 text-white active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {paying ? "Processing…" : !timingAck ? "Agree to the house rules to continue" : `Pay ₹${b.amount.toLocaleString("en-IN")} now`}
                </Button>
                <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1 justify-center">
                  <ShieldCheck size={12} className="text-orange-800" /> Free cancellation up to 24h before check-in
                </div>
              </>
            )}
          </div>
        </div>

        {paid && notif && (
          <div className="mt-6 bg-white border border-orange-300 rounded-2xl overflow-hidden">
            <div className="bg-orange-800 text-white px-6 py-4">
              <div className="text-xs tracking-widest uppercase font-bold opacity-90">Save your receipt</div>
              <div className="font-display font-bold text-xl mt-1">Send yourself a WhatsApp receipt</div>
            </div>
            <div className="p-6 space-y-4">
              {notif.email_sent_to_student === false && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                  <AlertOctagon size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <b>Email delivery pending</b> — the confirmation email couldn't be sent right now. <b>Bookmark this page or send the WhatsApp receipt below</b> to keep your verification code safe. All details are here, no need to worry.
                  </div>
                </div>
              )}
              <p className="text-sm text-slate-700">
                Tap below to send this booking's full details (verification code, dates, owner phone, hostel address) to your own WhatsApp — perfect for showing at check-in.
              </p>
              <a
                data-testid="wa-self-receipt-btn"
                href={notif.whatsapp_self_link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base"
              >
                <MessageCircle size={18} /> Send receipt to my WhatsApp
              </a>

              <div className="mt-4 pt-4 border-t border-dashed border-slate-200 grid sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1"><Phone size={10} /> Owner phone</div>
                  <div className="mt-2 font-semibold">{notif.owner_name}</div>
                  <a href={`tel:${notif.owner_phone}`} className="text-lg font-mono text-orange-800 hover:underline">{notif.owner_phone}</a>
                  {notif.whatsapp_owner_link && (
                    <a href={notif.whatsapp_owner_link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">
                      <MessageCircle size={12} /> WhatsApp owner
                    </a>
                  )}
                </div>
                <a href={notif.maps_link || "#"} target="_blank" rel="noopener noreferrer"
                   className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-orange-600 transition-colors group">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1"><MapPin size={10} /> Hostel location</div>
                  <div className="mt-2 text-sm font-semibold">{notif.location}</div>
                  <div className="text-xs text-orange-800 mt-1 inline-flex items-center gap-1">
                    Open in Google Maps <ExternalLink size={10} />
                  </div>
                </a>
              </div>
            </div>

            {/* Customer care strip */}
            <div className="px-6 py-4 bg-emerald-50 border-t border-emerald-200 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center flex-shrink-0"><Phone size={16} /></div>
              <div className="flex-1">
                <div className="text-[10px] tracking-widest uppercase font-bold text-emerald-800">HostelNow customer care</div>
                <div className="text-sm text-emerald-900 mt-0.5">Any issue with check-in or payment? Call or WhatsApp us.</div>
              </div>
              <div className="flex gap-2">
                <a href={`tel:${SUPPORT_PHONE}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700"><Phone size={12} /> {SUPPORT_PHONE_DISPLAY}</a>
                <a href={buildSupportWhatsAppLink(`Booking ${b.id.slice(0,8)}`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-emerald-500 text-emerald-800 text-xs font-bold hover:bg-emerald-100"><MessageCircle size={12} /> WhatsApp</a>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <Button variant="outline" onClick={() => nav("/dashboard")} className="rounded-full">Go to dashboard</Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
